import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Search, FileDown, Mail, Pencil, Trash2, Trophy, Clock3, XCircle, FileText, PenTool, RefreshCw, FileCheck2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const OutcomeBadge = ({ outcome }) => {
  if (outcome === 'won') return <Badge className="bg-green-100 text-green-800">Won</Badge>;
  if (outcome === 'lost') return <Badge className="bg-red-100 text-red-800">Lost</Badge>;
  if (outcome === 'pending') return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
  return <Badge variant="outline" className="text-slate-400">No outcome</Badge>;
};

const DS_LABELS = { processing: 'Processing', sent: 'Awaiting signature', delivered: 'Viewed', completed: 'Signed ✓', declined: 'Declined', voided: 'Voided' };
const ESignBadge = ({ contract }) => {
  const status = contract.esign_status || contract.docusign_status;
  if (!status) return null;
  const provider = contract.esign_provider === 'pandadoc' ? 'PandaDoc' : 'DocuSign';
  const cls = status === 'completed' ? 'bg-green-600 text-white' : status === 'declined' || status === 'voided' ? 'bg-red-100 text-red-800' : 'bg-indigo-100 text-indigo-800';
  return <Badge className={cls}>{provider}: {DS_LABELS[status] || status}</Badge>;
};

export default function CIHistory() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [repFilter, setRepFilter] = useState('all');
  const [emailContract, setEmailContract] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [dsContract, setDsContract] = useState(null);
  const [dsName, setDsName] = useState('');
  const [dsEmail, setDsEmail] = useState('');
  const [dsSending, setDsSending] = useState(false);
  const [dsRefreshing, setDsRefreshing] = useState('');

  const fetchContracts = () => {
    axios.get(API_URL + '/api/contracts?authorization=' + token)
      .then((r) => setContracts((r.data || []).filter((c) => c.contract_kind !== 'sme_multisite')))
      .catch(() => toast.error('Failed to load contracts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchContracts(); }, [token]);

  const reps = [...new Set(contracts.map((c) => c.created_by_name).filter(Boolean))].sort();

  const filtered = contracts.filter((c) => {
    if (outcomeFilter !== 'all') {
      if (outcomeFilter === 'none' && c.outcome) return false;
      if (outcomeFilter !== 'none' && c.outcome !== outcomeFilter) return false;
    }
    if (repFilter !== 'all' && c.created_by_name !== repFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [c.company_name, c.trading_name, c.agreement_number, c.contact_name, c.accounts_email, c.abn, JSON.stringify(c.sites || c.nmis || '')].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const stats = {
    total: contracts.length,
    won: contracts.filter((c) => c.outcome === 'won').length,
    lost: contracts.filter((c) => c.outcome === 'lost').length,
    pending: contracts.filter((c) => c.outcome === 'pending').length,
  };
  const decided = stats.won + stats.lost;
  const winRate = decided ? Math.round((stats.won / decided) * 100) : null;

  const updateOutcome = (id, outcome) => {
    const fd = new FormData();
    fd.append('outcome', outcome);
    fd.append('outcome_reason', '');
    axios.put(API_URL + '/api/contracts/' + id + '/outcome?authorization=' + token, fd)
      .then(() => { toast.success('Outcome set to ' + outcome); fetchContracts(); })
      .catch(() => toast.error('Failed to update outcome'));
  };

  const downloadPdf = (c) => {
    axios.get(API_URL + '/api/contracts/' + c.id + '/pdf?authorization=' + token, { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'NBE_Contract_' + (c.agreement_number || c.id.slice(0, 8)) + '.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Failed to generate PDF'));
  };

  const sendEmail = () => {
    if (!emailTo) { toast.error('Enter a recipient email'); return; }
    setEmailSending(true);
    const fd = new FormData();
    fd.append('customer_email', emailTo);
    fd.append('subject', '');
    fd.append('message', emailMessage);
    axios.post(API_URL + '/api/contracts/' + emailContract.id + '/send-email?authorization=' + token, fd)
      .then(() => { toast.success('Contract emailed'); setEmailContract(null); setEmailMessage(''); fetchContracts(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to send'))
      .finally(() => setEmailSending(false));
  };

  const sendDocuSign = () => {
    if (!dsEmail || !dsName) { toast.error('Enter the signer name and email'); return; }
    setDsSending(true);
    axios.post(API_URL + '/api/contracts/' + dsContract.id + '/docusign/send?authorization=' + token, {
      signer_email: dsEmail, signer_name: dsName, base_url: window.location.origin,
    })
      .then(() => { toast.success('Sent for signature via DocuSign'); setDsContract(null); fetchContracts(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'DocuSign send failed'))
      .finally(() => setDsSending(false));
  };

  const refreshDocuSign = (c) => {
    setDsRefreshing(c.id);
    axios.get(API_URL + '/api/contracts/' + c.id + '/esign/status?authorization=' + token)
      .then((r) => { toast.success(r.data.status_label || r.data.status); fetchContracts(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Status check failed'))
      .finally(() => setDsRefreshing(''));
  };

  const downloadSigned = (c) => {
    axios.get(API_URL + '/api/contracts/' + c.id + '/esign/signed-document?authorization=' + token, { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'NBE_Contract_' + (c.agreement_number || c.id.slice(0, 8)) + '_SIGNED.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Signed document not available yet'));
  };

  const handleDelete = (id) => {
    axios.delete(API_URL + '/api/contracts/' + id + '?authorization=' + token)
      .then(() => { toast.success('Contract deleted'); fetchContracts(); })
      .catch(() => toast.error('Failed to delete'));
  };

  const exportCsv = () => {
    const a = document.createElement('a');
    a.href = API_URL + '/api/contracts/export-csv?authorization=' + token;
    a.download = 'ci_contracts.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="ci-history-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">C&I History</h1>
            <p className="text-slate-500 mt-1">All generated C&I contracts{user?.role !== 'admin' ? ' created by you' : ''}</p>
          </div>
          <Button variant="outline" onClick={exportCsv} data-testid="ci-history-export-csv"><FileDown className="w-4 h-4 mr-2" />Export CSV</Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[['Total Contracts', stats.total, 'text-[#003366]'], ['Won', stats.won, 'text-green-600'], ['Lost', stats.lost, 'text-red-500'], ['Pending', stats.pending, 'text-yellow-600'], ['Win Rate', winRate != null ? winRate + '%' : '—', 'text-[#8CC63F]']].map(([label, val, color]) => (
            <Card key={label} data-testid={`ci-stat-${String(label).toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="pt-5 pb-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
                <p className={`text-2xl font-bold mt-1 ${color}`}>{val}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input placeholder="Search company, agreement #, contact, NMI..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ci-history-search" />
          </div>
          <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
            <SelectTrigger className="w-44" data-testid="ci-history-outcome-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outcomes</SelectItem>
              <SelectItem value="won">Won</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="none">No outcome</SelectItem>
            </SelectContent>
          </Select>
          {reps.length > 1 && (
            <Select value={repFilter} onValueChange={setRepFilter}>
              <SelectTrigger className="w-48" data-testid="ci-history-rep-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reps</SelectItem>
                {reps.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {loading ? (
          <p className="text-slate-500 text-sm py-8 text-center">Loading...</p>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No contracts match your filters</p></CardContent></Card>
        ) : (
          <div className="space-y-3" data-testid="ci-history-list">
            {filtered.map((c) => (
              <Card key={c.id} className={'border-l-4 hover:shadow-md transition-shadow ' + (c.outcome === 'won' ? 'border-l-green-500' : c.outcome === 'lost' ? 'border-l-red-400' : c.outcome === 'pending' ? 'border-l-yellow-400' : 'border-l-[#005394]')} data-testid={`ci-history-row-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="space-y-1 flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{c.company_name || c.trading_name || 'Untitled'}</h3>
                        <Badge variant="outline" className="text-xs">{c.agreement_number || c.id.slice(0, 8)}</Badge>
                        {c.contract_state && <Badge className="bg-blue-100 text-blue-800">{c.contract_state === 'QLD_SA' ? 'QLD/SA' : c.contract_state}</Badge>}
                        {c.status === 'sent' ? <Badge className="bg-green-100 text-green-800">Sent</Badge> : <Badge variant="outline">Draft</Badge>}
                        <OutcomeBadge outcome={c.outcome} />
                        <ESignBadge contract={c} />
                      </div>
                      <p className="text-xs text-slate-500">
                        {c.contact_name && <span>{c.contact_name} | </span>}
                        {c.accounts_email && <span>{c.accounts_email} | </span>}
                        {c.created_by_name && <span>Rep: {c.created_by_name} | </span>}
                        Created {fmtDate(c.created_at)}
                        {c.sent_at && <span> | Emailed {fmtDate(c.sent_at)} to {c.sent_to}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <div className="flex gap-0.5 mr-1 border-r pr-2">
                        <button onClick={() => updateOutcome(c.id, 'won')} className={'p-1.5 rounded transition-colors ' + (c.outcome === 'won' ? 'bg-green-100 text-green-700' : 'text-slate-300 hover:text-green-600 hover:bg-green-50')} title="Mark Won" data-testid={`ci-hist-won-${c.id}`}><Trophy className="w-4 h-4" /></button>
                        <button onClick={() => updateOutcome(c.id, 'pending')} className={'p-1.5 rounded transition-colors ' + (c.outcome === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'text-slate-300 hover:text-yellow-600 hover:bg-yellow-50')} title="Mark Pending" data-testid={`ci-hist-pending-${c.id}`}><Clock3 className="w-4 h-4" /></button>
                        <button onClick={() => updateOutcome(c.id, 'lost')} className={'p-1.5 rounded transition-colors ' + (c.outcome === 'lost' ? 'bg-red-100 text-red-700' : 'text-slate-300 hover:text-red-600 hover:bg-red-50')} title="Mark Lost" data-testid={`ci-hist-lost-${c.id}`}><XCircle className="w-4 h-4" /></button>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => downloadPdf(c)} title="Download PDF" data-testid={`ci-hist-pdf-${c.id}`}><FileDown className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEmailContract(c); setEmailTo(c.accounts_email || ''); }} title="Email contract" data-testid={`ci-hist-email-${c.id}`}><Mail className="w-4 h-4" /></Button>
                      {(c.esign_document_id || c.docusign_envelope_id) ? (
                        (c.esign_status || c.docusign_status) === 'completed' ? (
                          <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700" onClick={() => downloadSigned(c)} title="Download signed contract" data-testid={`ci-hist-ds-signed-${c.id}`}><FileCheck2 className="w-4 h-4" /></Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700" onClick={() => refreshDocuSign(c)} disabled={dsRefreshing === c.id} title="Refresh signature status" data-testid={`ci-hist-ds-refresh-${c.id}`}><RefreshCw className={'w-4 h-4' + (dsRefreshing === c.id ? ' animate-spin' : '')} /></Button>
                        )
                      ) : (
                        <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700" onClick={() => { setDsContract(c); setDsName(c.customer_sign_name || c.contact_name || ''); setDsEmail(c.accounts_email || ''); }} title="Send for eSignature" data-testid={`ci-hist-docusign-${c.id}`}><PenTool className="w-4 h-4" /></Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => navigate('/contracts', { state: { editContractId: c.id } })} title="Edit in Contract Generator" data-testid={`ci-hist-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                      {user?.role === 'admin' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" title="Delete" data-testid={`ci-hist-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete contract?</AlertDialogTitle>
                              <AlertDialogDescription>Contract {c.agreement_number || c.id.slice(0, 8)} for "{c.company_name || 'Untitled'}" will be permanently deleted.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(c.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={!!emailContract} onOpenChange={(o) => !o && setEmailContract(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Email Contract {emailContract?.agreement_number}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Recipient Email</Label>
                <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="customer@company.com.au" data-testid="ci-hist-email-to" />
              </div>
              <div>
                <Label>Message (optional)</Label>
                <Textarea value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} rows={4} placeholder="Leave blank for the default message" data-testid="ci-hist-email-message" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailContract(null)}>Cancel</Button>
              <Button onClick={sendEmail} disabled={emailSending} className="bg-[#015492] hover:bg-[#013f6e]" data-testid="ci-hist-email-send">{emailSending ? 'Sending...' : 'Send Contract'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={!!dsContract} onOpenChange={(o) => !o && setDsContract(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Send for eSignature — {dsContract?.agreement_number}</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500">The customer receives an email from the active provider (DocuSign or PandaDoc — set in Settings → eSignature), signs electronically, and the signed contract is stored back here automatically.</p>
            <div className="space-y-3">
              <div>
                <Label>Signer Name</Label>
                <Input value={dsName} onChange={(e) => setDsName(e.target.value)} placeholder="Jane Smith" data-testid="ds-signer-name" />
              </div>
              <div>
                <Label>Signer Email</Label>
                <Input value={dsEmail} onChange={(e) => setDsEmail(e.target.value)} placeholder="jane@company.com.au" data-testid="ds-signer-email" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDsContract(null)}>Cancel</Button>
              <Button onClick={sendDocuSign} disabled={dsSending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ds-send-btn">{dsSending ? 'Sending...' : 'Send for Signature'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
